import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { navigate } from 'gatsby';
import { Box, Container, Divider, Heading } from '@hover/blueprint';
import { Layout, SearchBar, UtilityMenuBanner } from '../../components/common';
import { BlogCategoriesTabs, BlogHeroPost } from '../../components/blog';
import { BLOG_CATEGORIES } from '../../constants';
import type { IBlogCategory } from '../../interfaces/IBlogCategory';
import type { IBlogPost } from '../../interfaces/IBlogPost';
import NoResultsForSearch from './search/noResults';
import SearchResultHeader from './search/resultHeader';
import { IBlogReducerStateData } from '../../interfaces/IBlogReducer';
import { TBlogReducerAction } from '../../utils/reducers/blog-reducer';

interface IBlogLayoutProps {
  title: string;
  description: string;
  pageCategory: IBlogCategory;
  activeCategory: IBlogCategory;
  firstBlogPost: IBlogPost;
  isLoading: boolean;
  onCategoryChange: (category: IBlogCategory) => void;
  searchKeyword: string;
  setSearchKeyword: (keyword: string) => void;
  categoriesData: IBlogReducerStateData;
  pageNumber: number;
  blogReducerDispatch: React.Dispatch<TBlogReducerAction>;
}

const BASE_CATEGORY = BLOG_CATEGORIES[0];

const BlogLayout: React.FC<PropsWithChildren<IBlogLayoutProps>> = ({
  title,
  description,
  pageCategory,
  activeCategory,
  firstBlogPost,
  isLoading,
  onCategoryChange,
  searchKeyword,
  setSearchKeyword,
  categoriesData,
  pageNumber,
  blogReducerDispatch,
  children,
}) => {
  const categoryHeadingRef = useRef<HTMLHeadingElement>(null);
  const isSearchActive = activeCategory.key === 'search';

  let canShowCategories = true;

  const canShowBlogCards =
    categoriesData &&
    activeCategory &&
    categoriesData[activeCategory.value] &&
    categoriesData[activeCategory.value][pageNumber]?.length;

  if (activeCategory.value === 'Search') {
    if (!isLoading) {
      canShowCategories =
        !!categoriesData[activeCategory.value] &&
        !!categoriesData[activeCategory.value][pageNumber]?.length;
    }
  }

  const resetCategories = () => {
    setSearchKeyword('');
    onCategoryChange(pageCategory);
  };

  const handleSubmitSearch = ({
    keyword: inputKeyword,
  }: {
    keyword: string;
  }) => {
    if (inputKeyword) {
      blogReducerDispatch({ type: 'reset-search' });
      setSearchKeyword(inputKeyword);
      onCategoryChange(BLOG_CATEGORIES.find(item => item.key === 'search')!);
    } else {
      resetCategories();
    }
  };

  const handleCategoryNavigation = (category: IBlogCategory) => {
    if (category.key === BASE_CATEGORY.key) void navigate('/blog');
    else void navigate(`/blog/${category.key}`);
  };

  useEffect(() => {
    if (activeCategory.key !== BASE_CATEGORY.key)
      categoryHeadingRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
  }, []);

  return (
    <Layout
      title={title}
      description={description}
      bannerElement={<UtilityMenuBanner />}
    >
      <Container maxW="1248px" px={500}>
        <SearchBar
          handleSubmit={handleSubmitSearch}
          hideBottomDivider={isSearchActive}
        />
        {!isSearchActive && <BlogHeroPost my={700} post={firstBlogPost} />}
        {canShowCategories ? (
          <BlogCategoriesTabs
            activeCategory={activeCategory}
            handleClick={handleCategoryNavigation}
          />
        ) : (
          <Divider color="neutral.300" />
        )}
        <Box pb="90px" display="flex" flexDir="column">
          {!isSearchActive && activeCategory !== BASE_CATEGORY ? (
            <Box my={800} display={{ base: 'none', tablet: 'flex' }}>
              <Heading
                ref={categoryHeadingRef}
                as="h2"
                size={600}
                textTransform="capitalize"
                marginBottom={0}
                fontWeight={400}
              >
                {activeCategory.value}
              </Heading>
            </Box>
          ) : (
            <Box mb={{ base: 0, tablet: 700 }} />
          )}
          {isSearchActive && canShowBlogCards && (
            <SearchResultHeader searchKeyword={searchKeyword} />
          )}
          {children}
          {isLoading && <Box pb="100vh" />}
          {!canShowBlogCards && isSearchActive && !isLoading && (
            <NoResultsForSearch
              resetSearch={resetCategories}
              searchKeyword={searchKeyword}
            />
          )}
        </Box>
      </Container>
    </Layout>
  );
};

export default BlogLayout;
